import * as THREE from 'three/webgpu';

import { mulberry32, hashSeed } from '../math/rng.js';

/**
 * @file The shop-sign atlas: one canvas holding a GRID of painted fascia boards,
 * each with a name set in it, which any level can sample one cell at a time from
 * an ordinary UV'd quad in its merged, baked geometry.
 *
 * Generic engine asset, exactly like `windowMasks.js` next door — nothing in
 * here knows what a shop is. The caller hands in the boards it wants
 * ({@link SignBoard}) and gets back a texture plus the UV rect of each one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A GRID OF OPAQUE BOARDS, AND NOT TRANSPARENT LETTERS
 * ─────────────────────────────────────────────────────────────────────────────
 * The obvious build is white text on a transparent background, alpha-tested over
 * a coloured prism. It is the wrong one here for three separate reasons:
 *
 *   • The merged city geometry is OPAQUE. An alpha-tested slot would be the only
 *     cut-out surface in the level, and cut-outs do not sort — the moment one
 *     ends up in front of the shopfront glazing it flickers.
 *   • Text is the one thing in this project that MUST mipmap. A 2 m sign read at
 *     40 m is a handful of texels tall, and un-mipmapped letterforms crawl. The
 *     lit-window atlas can be `NearestFilter` because a mask has no letterforms;
 *     this cannot.
 *   • Mipmapping an atlas bleeds neighbouring cells into each other at the small
 *     levels. With OPAQUE boards the bleed pulls in the neighbour's BOARD
 *     COLOUR through a gutter painted the cell's own background, which is
 *     invisible. With alpha it would pull in the neighbour's LETTERS.
 *
 * So a cell is a finished sign — board, border, text — and the quad that samples
 * it needs no material of its own beyond "baked colour x this map".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAMPLING RULES (get these wrong and it looks like a texture bug)
 * ─────────────────────────────────────────────────────────────────────────────
 * • `ClampToEdgeWrapping` on BOTH axes. Unlike the window atlas nothing here
 *   tiles: one cell is one sign, sampled exactly once across the quad.
 * • `generateMipmaps: true`, `LinearFilter` / `LinearMipmapLinearFilter` — see
 *   above. Anisotropy is the caller's to set from the renderer's maximum; signs
 *   are read at a glancing 52-degree pitch and it is the single biggest quality
 *   win available here.
 * • Every cell is painted with a {@link SIGN_ATLAS_DEFAULTS.gutter}px border of
 *   its own background colour and {@link signAtlasUV} then insets the rect by
 *   that same gutter, so no mip level can ever reach a neighbour's ink.
 * • Canvas rows run top-down and texture `v` runs bottom-up (`flipY` defaults to
 *   true), so {@link signAtlasUV} returns v ALREADY FLIPPED, like the window
 *   atlas. Callers use the numbers as-is.
 *
 * @module engine/assets/signText
 */

/** @typedef {{u0: number, v0: number, u1: number, v1: number}} UVRect */

/**
 * One board to paint. Colours are CSS strings — this module draws into a canvas
 * and never touches the project's linear-space palette, so there is no second
 * hex->linear conversion hiding in here.
 *
 * @typedef {Object} SignBoard
 * @property {string} text The name. Set in caps; long names are condensed to fit
 *   rather than wrapped or clipped.
 * @property {string} bg Board colour.
 * @property {string} fg Letter colour.
 * @property {string} [accent] Rule/border colour. Defaults to `fg`.
 * @property {'plain'|'ruled'|'boxed'} [style='ruled'] Board furniture.
 */

/**
 * @typedef {Object} SignCell
 * @property {number} index
 * @property {number} x @property {number} y Pixel origin, TOP-LEFT (canvas space).
 * @property {number} w @property {number} h Pixel size.
 */

/**
 * @typedef {Object} SignLayout
 * @property {number} width @property {number} height Texture size, pixels.
 * @property {number} cols @property {number} rows
 * @property {number} cellW @property {number} cellH
 * @property {number} gutter
 * @property {SignCell[]} cells
 */

/** Defaults, exported so tests and callers agree on the numbers. */
export const SIGN_ATLAS_DEFAULTS = Object.freeze({
  /**
   * 8:1, because a shopfront FASCIA is a long thin strip — a 3.2 m sign on a
   * 0.4 m band. Squarer cells would have to be letterboxed onto the board and
   * the text would end up half the size for the same texture memory. 64 px of
   * height puts the cap height at 28 px, which still resolves at the ~25 m the
   * player reads a shop from.
   */
  cellW: 512,
  cellH: 64,
  /** Cells per row. 4 keeps a 48-board atlas at 2048x768. */
  cols: 4,
  /** Pixels of own-background border round every cell — the anti-bleed margin. */
  gutter: 2,
  /** Cap height as a fraction of the cell, before any condensing. */
  textH: 0.44,
  /** Fraction of the cell width the text may occupy. */
  textW: 0.86,
});

/**
 * The pixel layout. PURE — no canvas, no three.js, so the tests can check the
 * UV algebra in node. (Same split as `windowAtlasLayout`.)
 *
 * @param {number} count How many boards.
 * @param {{cellW?: number, cellH?: number, cols?: number, gutter?: number}} [o]
 * @returns {SignLayout}
 */
export function signAtlasLayout(count, o = {}) {
  const D = SIGN_ATLAS_DEFAULTS;
  const n = Math.max(1, Math.round(count));
  const cellW = Math.max(8, Math.round(o.cellW ?? D.cellW));
  const cellH = Math.max(8, Math.round(o.cellH ?? D.cellH));
  const cols = Math.max(1, Math.min(n, Math.round(o.cols ?? D.cols)));
  const gutter = Math.max(0, Math.round(o.gutter ?? D.gutter));
  const rows = Math.ceil(n / cols);

  /** @type {SignCell[]} */
  const cells = [];
  for (let i = 0; i < n; i++) {
    cells.push({
      index: i,
      x: (i % cols) * cellW,
      y: Math.floor(i / cols) * cellH,
      w: cellW,
      h: cellH,
    });
  }
  return { width: cols * cellW, height: rows * cellH, cols, rows, cellW, cellH, gutter, cells };
}

/**
 * UV rect of one cell, in TEXTURE space (v already flipped for `flipY: true`),
 * inset by the gutter so no mip level can reach the neighbouring cell.
 *
 * @param {SignLayout} layout
 * @param {number} index Wraps, like `windowAtlasUV`.
 * @returns {UVRect}
 */
export function signAtlasUV(layout, index) {
  const n = layout.cells.length;
  const c = layout.cells[((index % n) + n) % n];
  const W = layout.width;
  const H = layout.height;
  const g = layout.gutter;
  // Canvas y grows downward; texture v grows upward.
  return {
    u0: (c.x + g) / W,
    v0: 1 - (c.y + c.h - g) / H,
    u1: (c.x + c.w - g) / W,
    v1: 1 - (c.y + g) / H,
  };
}

/**
 * @typedef {Object} SignAtlas
 * @property {THREE.CanvasTexture} texture
 * @property {SignLayout} layout
 * @property {number} count
 * @property {(index: number) => UVRect} uvFor
 * @property {() => void} dispose
 */

/**
 * Paints the atlas.
 *
 * @param {Object} o
 * @param {ReadonlyArray<SignBoard>} o.boards
 * @param {number} [o.cellW=256] @param {number} [o.cellH=64]
 * @param {number} [o.cols=4] @param {number} [o.gutter=2]
 * @param {number} [o.anisotropy=1] From `renderer.capabilities.getMaxAnisotropy()`.
 * @param {string|number} [o.seed='signs'] Deterministic — same seed, same wear.
 * @param {string} [o.font='"Arial Black", "Helvetica Neue", Arial, sans-serif']
 * @param {string} [o.name='signText']
 * @returns {SignAtlas}
 */
export function makeSignAtlas(o) {
  const boards = o.boards ?? [];
  if (boards.length === 0) throw new Error('makeSignAtlas: needs at least one board');
  const layout = signAtlasLayout(boards.length, o);

  if (typeof document === 'undefined') {
    throw new Error('makeSignAtlas: needs a DOM canvas — use signAtlasLayout() in node');
  }

  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const g = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));

  const rng = mulberry32(hashSeed(o.seed ?? 'signs'));
  const font = o.font ?? '"Arial Black", "Helvetica Neue", Arial, sans-serif';

  for (const cell of layout.cells) {
    drawBoard(g, cell, boards[cell.index], rng, font);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = o.name ?? 'signText';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = Math.max(1, Math.round(o.anisotropy ?? 1));
  texture.needsUpdate = true;

  return {
    texture,
    layout,
    count: boards.length,
    uvFor: (i) => signAtlasUV(layout, i),
    dispose() {
      texture.dispose();
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One finished sign: board, furniture, name. Fills the WHOLE cell including the
 * gutter — the gutter is background on purpose, so a mip level that bleeds
 * across the cell edge picks up this board's own colour.
 *
 * @param {CanvasRenderingContext2D} g
 * @param {SignCell} cell
 * @param {SignBoard} board
 * @param {import('../types.js').Rng} rng
 * @param {string} font
 */
function drawBoard(g, cell, board, rng, font) {
  const D = SIGN_ATLAS_DEFAULTS;
  const accent = board.accent ?? board.fg;
  const style = board.style ?? 'ruled';

  g.save();
  g.beginPath();
  g.rect(cell.x, cell.y, cell.w, cell.h);
  g.clip();

  // 1. the board.
  g.fillStyle = board.bg;
  g.fillRect(cell.x, cell.y, cell.w, cell.h);

  // 2. a vertical wash, so a flat-lit fascia still has a top-to-bottom gradient
  //    at the one pitch the player ever sees it from. ALWAYS drawn, so the rng
  //    below spends the same draws on every board.
  const wash = g.createLinearGradient(0, cell.y, 0, cell.y + cell.h);
  wash.addColorStop(0, 'rgba(255,255,255,0.10)');
  wash.addColorStop(0.55, 'rgba(255,255,255,0)');
  wash.addColorStop(1, 'rgba(0,0,0,0.16)');
  g.fillStyle = wash;
  g.fillRect(cell.x, cell.y, cell.w, cell.h);

  // 3. furniture. Two rng draws, unconditionally, whatever the style — the
  //    project's stream-stability rule applied to a canvas.
  const inset = Math.max(2, Math.round(cell.h * (0.07 + rng() * 0.04)));
  const ruleW = Math.max(1, Math.round(cell.h * (0.02 + rng() * 0.02)));
  if (style === 'boxed') {
    g.strokeStyle = accent;
    g.lineWidth = ruleW;
    g.strokeRect(
      cell.x + inset + ruleW / 2,
      cell.y + inset + ruleW / 2,
      cell.w - (inset + ruleW / 2) * 2,
      cell.h - (inset + ruleW / 2) * 2,
    );
  } else if (style === 'ruled') {
    g.fillStyle = accent;
    g.fillRect(cell.x + inset, cell.y + inset, cell.w - inset * 2, ruleW);
    g.fillRect(cell.x + inset, cell.y + cell.h - inset - ruleW, cell.w - inset * 2, ruleW);
  }

  // 4. the name. Condensed to fit rather than clipped: a shop called
  //    "HOLLOWAY & DAUGHTERS" gets narrow letters, never half a word.
  const text = (board.text ?? '').toUpperCase();
  if (text) {
    const px = Math.max(6, Math.round(cell.h * D.textH));
    g.font = `${px}px ${font}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const maxW = cell.w * D.textW;
    const w = g.measureText(text).width || 1;
    const squeeze = Math.min(1, maxW / w);

    g.translate(cell.x + cell.w / 2, cell.y + cell.h / 2);
    g.scale(squeeze, 1);
    // A soft drop shadow is what separates letters from a board of similar
    // value once the mip chain has taken the edges off.
    g.fillStyle = 'rgba(0,0,0,0.35)';
    g.fillText(text, 0, Math.max(1, px * 0.06));
    g.fillStyle = board.fg;
    g.fillText(text, 0, 0);
  }

  g.restore();
}
